/**
 * ActivityProvider — typed event emitter for Studio activity log.
 *
 * Replaces the ephemeral addAct() useState in SimpleLeftPanel.
 * Phase 2: in-memory only (capped at 200 entries), survives navigation
 * within the session but resets on page refresh.
 * Phase 9: server persistence via POST /api/activity (fire-and-forget).
 * Phase 10: localStorage persistence keyed by active style ID so logs
 * survive navigation and page refresh.
 *
 * Usage:
 *   const { emit } = useActivity();
 *   emit('IMAGE_UPLOADED', '📎 Uploaded jacket_front.jpg', { styleId, slot: 'design_sketch' });
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ActivityEventType } from "../../../shared/db/types.js";
import { getFreshToken } from "./auth.js";
import { useActiveObject } from "./ActiveObjectProvider.js";

export interface ActivityLogEntry {
  id: string;
  eventType: ActivityEventType;
  label: string;
  timestamp: string;
  payload?: Record<string, unknown>;
}

export interface ActivityContextValue {
  activityLog: readonly ActivityLogEntry[];
  emit: (eventType: ActivityEventType, label: string, payload?: Record<string, unknown>) => void;
  clearLog: () => void;
}

const ActivityCtx = createContext<ActivityContextValue | null>(null);

export function useActivity(): ActivityContextValue {
  const ctx = useContext(ActivityCtx);
  if (!ctx) throw new Error("useActivity must be called inside <ActivityProvider>");
  return ctx;
}

export function ActivityProvider({ children }: { children: ReactNode }) {
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);

  const { activeStyleId } = useActiveObject();
  const activityStorageKey = activeStyleId
    ? `studio_activity:${activeStyleId}`
    : "studio_activity_landing";
  const activityStorageKeyRef = useRef(activityStorageKey);

  useEffect(() => {
    activityStorageKeyRef.current = activityStorageKey;
    try {
      const raw = localStorage.getItem(activityStorageKey);
      setActivityLog(raw ? (JSON.parse(raw) as ActivityLogEntry[]) : []);
    } catch { setActivityLog([]); }
  }, [activityStorageKey]);

  useEffect(() => {
    if (activityStorageKeyRef.current !== activityStorageKey) return;
    try {
      localStorage.setItem(activityStorageKey, JSON.stringify(activityLog.slice(0, 200)));
    } catch { /* ignore */ }
  }, [activityLog, activityStorageKey]);

  const emit = useCallback((
    eventType: ActivityEventType,
    label: string,
    payload?: Record<string, unknown>,
  ) => {
    const entry: ActivityLogEntry = {
      id: crypto.randomUUID(),
      eventType,
      label,
      timestamp: new Date().toISOString(),
      payload,
    };
    setActivityLog(prev => [entry, ...prev].slice(0, 200));
    // Phase 9: fire-and-forget server persistence
    getFreshToken().then(token => {
      if (!token) return;
      fetch("/api/activity", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ eventType: entry.eventType, label: entry.label, payload: entry.payload ?? null }),
      }).catch(() => { /* non-blocking */ });
    }).catch(() => { /* non-blocking */ });
  }, []);

  const clearLog = useCallback(() => setActivityLog([]), []);

  return (
    <ActivityCtx.Provider value={{ activityLog, emit, clearLog }}>
      {children}
    </ActivityCtx.Provider>
  );
}
